/**
 * Extracted from `components/assessment-structure/blueprint-panel.tsx` so Fast Refresh can preserve that file's
 * component state. A component file that also exports plain values makes
 * React remount the whole tree on every edit; these values have no JSX in
 * them, so they belong beside the component rather than inside it.
 */

import { DEFAULT_EXAM_CATEGORIES } from "@/lib/exam-categories/defaults"
import { findCategory, getRootCategoryId } from "@/lib/exam-categories/tree"
import { labelForContentId, skillLabelForContentId } from "@/lib/blueprint/mapping"
import type { AssessmentBlueprintGap } from "@/lib/mock/assessment-add-questions"

export type BlueprintCoverageState = "under" | "met" | "over"

export interface BlueprintGapProgress {
  gap: AssessmentBlueprintGap
  scopeValue: string
  filled: number
  satisfied: boolean
}

export function coverageState(
  filled: number,
  target: number
): BlueprintCoverageState {
  if (filled > target) return "over"
  if (filled >= target) return "met"
  return "under"
}

export function rollupState(
  states: readonly BlueprintCoverageState[]
): BlueprintCoverageState {
  if (states.some((state) => state === "under")) return "under"
  if (states.some((state) => state === "over")) return "over"
  return "met"
}

export interface SkillNode {
  categoryId: string
  label: string
  filled: number
  target: number
  state: BlueprintCoverageState
  blooms: BloomNode[]
}

export interface CategoryNode {
  categoryId: string
  topic: string
  filled: number
  target: number
  state: BlueprintCoverageState
  skills: SkillNode[]
}

export interface BloomNode {
  bloom: string
  filled: number
  target: number
  state: BlueprintCoverageState
  leaves: LeafNode[]
}

export interface LeafNode {
  entry: BlueprintGapProgress
  filled: number
  target: number
  state: BlueprintCoverageState
  label: string
}

export function groupCoverage(gapProgress: BlueprintGapProgress[]): CategoryNode[] {
  const byParent = new Map<
    string,
    Map<string, Map<string, LeafNode[]>>
  >()

  gapProgress.forEach((entry) => {
    const { gap, filled } = entry
    const state = coverageState(filled, gap.targetCount)
    const leaf: LeafNode = {
      entry,
      filled,
      target: gap.targetCount,
      state,
      label: `${gap.difficulty} ${gap.questionType}`,
    }
    const parentId =
      getRootCategoryId(DEFAULT_EXAM_CATEGORIES, gap.categoryId) ??
      gap.categoryId
    let skills = byParent.get(parentId)
    if (!skills) {
      skills = new Map()
      byParent.set(parentId, skills)
    }
    let blooms = skills.get(gap.categoryId)
    if (!blooms) {
      blooms = new Map()
      skills.set(gap.categoryId, blooms)
    }
    const leaves = blooms.get(gap.bloom) ?? []
    leaves.push(leaf)
    blooms.set(gap.bloom, leaves)
  })

  return Array.from(byParent.entries()).map(([parentId, skillMap]) => {
    const skills: SkillNode[] = Array.from(skillMap.entries()).map(
      ([categoryId, bloomMap]) => {
        const blooms: BloomNode[] = Array.from(bloomMap.entries()).map(
          ([bloom, leaves]) => {
            const filled = leaves.reduce((sum, leaf) => sum + leaf.filled, 0)
            const target = leaves.reduce((sum, leaf) => sum + leaf.target, 0)
            return {
              bloom,
              filled,
              target,
              state: rollupState(leaves.map((leaf) => leaf.state)),
              leaves,
            }
          }
        )
        const filled = blooms.reduce((sum, node) => sum + node.filled, 0)
        const target = blooms.reduce((sum, node) => sum + node.target, 0)
        return {
          categoryId,
          label: skillLabelForContentId(categoryId),
          filled,
          target,
          state: rollupState(blooms.map((node) => node.state)),
          blooms,
        }
      }
    )
    const filled = skills.reduce((sum, node) => sum + node.filled, 0)
    const target = skills.reduce((sum, node) => sum + node.target, 0)
    return {
      categoryId: parentId,
      topic: findCategory(DEFAULT_EXAM_CATEGORIES, parentId)?.name ?? parentId,
      filled,
      target,
      state: rollupState(skills.map((node) => node.state)),
      skills,
    }
  })
}

export function blueprintCoverageTree(gapProgress: BlueprintGapProgress[]) {
  return groupCoverage(gapProgress)
}

export function blueprintCoverageState(
  filled: number,
  target: number
): BlueprintCoverageState {
  return coverageState(filled, target)
}
